import { useContext } from 'react';
import { DocumentContext, isBoundDocument, isSelectionInsideFieldContent } from '@modules/documents';
import { TEMPLATE_FIELD_DROPDOWN_NAME } from '@modules/documents/doc-engine/templateFields';
import { ToastContext } from '@shared/contexts';
import { NEW_COMMENT_ID, SELECTION_MARK_NAME, SELECTION_NODE_NAME } from '../constants';

// prosemirror helpers:
import type { Editor } from '@tiptap/core';
import { NodeSelection, TextSelection, EditorState } from '@tiptap/pm/state';
import { CellSelection } from '@tiptap/pm/tables';
import { Node as PMNode, ResolvedPos } from '@tiptap/pm/model';

const LIST_NODE_NAMES = ['bulletList', 'orderedListWithStart', 'taskList'] as const;
const isListNodeName = (name: string) => LIST_NODE_NAMES.includes(name as any);

type TListInfo = {
  depth: number;
  posBefore: number;
  node: PMNode;
};

const isParagraphOnlySelection = ($from: ResolvedPos, $to: ResolvedPos): boolean =>
  $from.sameParent($to) && $from.parent.type.name === 'paragraph';

// Nearest list ancestor (inner-most)
const getNearestListInfo = ($pos: ResolvedPos): TListInfo | null => {
  for (let d = $pos.depth; d > 0; d--) {
    const node = $pos.node(d);
    if (isListNodeName(node.type.name)) {
      return {
        depth: d,
        posBefore: $pos.before(d),
        node,
      };
    }
  }
  return null;
};

// Root list ancestor (outer-most within the subtree)
const getRootListInfo = ($pos: ResolvedPos): TListInfo | null => {
  let info: TListInfo | null = null;
  for (let d = $pos.depth; d > 0; d--) {
    const node = $pos.node(d);
    if (isListNodeName(node.type.name)) {
      info = {
        depth: d,
        posBefore: $pos.before(d),
        node,
      };
    }
  }
  return info;
};

/**
 * Normalize table CellSelection -> NodeSelection(table).
 */
const normalizeTableSelection = (editor: Editor, state: EditorState): EditorState => {
  const { selection } = state;

  if (!(selection instanceof CellSelection)) return state;

  const $cell = selection.$anchorCell;

  let tableDepth: number | null = null;
  for (let d = $cell.depth; d > 0; d--) {
    if ($cell.node(d).type.name === 'table') {
      tableDepth = d;
      break;
    }
  }

  if (tableDepth == null) return state;

  const tablePos = $cell.before(tableDepth);
  const tr = state.tr.setSelection(NodeSelection.create(state.doc, tablePos));
  editor.view.dispatch(tr);

  return editor.state;
};

/**
 * Normalize list selections:
 * - same list node -> NodeSelection(list)
 * - resuming lists (same listId) -> TextSelection from first list to last list
 * - mixed nested lists under same outer list root -> NodeSelection(root list)
 */
const normalizeListSelection = (editor: Editor, state: EditorState): EditorState => {
  const { selection, doc } = state;
  const { $from, $to } = selection;

  const fromNearest = getNearestListInfo($from);
  const toNearest = getNearestListInfo($to);

  // No list involvement at either end ⇒ nothing to normalize
  if (!fromNearest || !toNearest) return state;

  // For single-paragraph selections we still want marks
  if (isParagraphOnlySelection($from, $to)) return state;

  // 2a) Same physical list node
  if (fromNearest.posBefore === toNearest.posBefore) {
    const tr = state.tr.setSelection(NodeSelection.create(doc, fromNearest.posBefore));
    editor.view.dispatch(tr);
    return editor.state;
  }

  // 2b) Resuming lists: same logical listId across separate list nodes
  const fromListId = fromNearest.node.attrs.listId;
  const toListId = toNearest.node.attrs.listId;

  if (fromListId && toListId && fromListId === toListId) {
    const fromPos = fromNearest.posBefore;
    const toPos = toNearest.posBefore + toNearest.node.nodeSize;

    const tr = state.tr.setSelection(TextSelection.create(doc, fromPos, toPos));
    editor.view.dispatch(tr);
    return editor.state;
  }

  // 2c) Mixed nested lists under the same outer list root
  const fromRoot = getRootListInfo($from);
  const toRoot = getRootListInfo($to);

  if (fromRoot && toRoot && fromRoot.posBefore === toRoot.posBefore) {
    const tr = state.tr.setSelection(NodeSelection.create(doc, fromRoot.posBefore));
    editor.view.dispatch(tr);
    return editor.state;
  }

  // Otherwise, leave as is
  return state;
};

const useCreateSelection = () => {
  const documentContext = useContext(DocumentContext);
  const { show } = useContext(ToastContext);

  return (): boolean => {
    if (!documentContext) return true;

    const { editor } = documentContext;

    // 1) Normalize special selections (tables, lists)
    let state: EditorState = editor.state;
    state = normalizeTableSelection(editor, state);
    state = normalizeListSelection(editor, state);

    // 2) Work with the (possibly normalized) selection
    const { selection, doc, schema } = state;
    const { from, to, empty, $from, $to } = selection;

    if (empty) return true;

    // Check if selection already contains a selection node
    let containsSelectionElem = false;
    doc.nodesBetween(from, to, (node) => {
      if (node.type.name === SELECTION_NODE_NAME) containsSelectionElem = true;
    });

    if (!containsSelectionElem) {
      const selectionMark = schema.marks[SELECTION_MARK_NAME];
      containsSelectionElem = doc.rangeHasMark(from, to, selectionMark);
    }

    if (containsSelectionElem) {
      show('warning', `Please ensure the selected content doesn't already contain another comment.`);
      return false;
    }

    // Bound documents: comments anchor inside a field's content (the only instance-owned content).
    // Same mark-vs-node split as the normal path below, scoped to one field: inline content (text,
    // references, inline blocks) -> mark; block content (images, tables, multi-block spans) -> node.
    if (isBoundDocument(state)) {
      // A dropdown atom has no inner content, so the selection covers it - mark the node itself.
      const dropdownType = schema.nodes[TEMPLATE_FIELD_DROPDOWN_NAME];
      const selectedNode = doc.nodeAt(from);
      if (selectedNode && selectedNode.type === dropdownType && from + selectedNode.nodeSize === to) {
        editor.chain().focus().wrapSelectionMark(NEW_COMMENT_ID).run();
        return true;
      }
      if (!isSelectionInsideFieldContent(state)) {
        show('warning', 'Comments can only be added inside a field.');
        return false;
      }
      if ($from.sameParent($to) && $from.parent.inlineContent) {
        editor.chain().focus().wrapSelectionMark(NEW_COMMENT_ID).run();
      } else {
        editor.chain().focus().wrapSelectionNode(NEW_COMMENT_ID).run();
      }
      return true;
    }

    // Decide: mark vs node
    const isSelectionMark = isParagraphOnlySelection($from, $to);

    if (isSelectionMark) {
      editor.chain().focus().wrapSelectionMark(NEW_COMMENT_ID).run();
    } else {
      editor.chain().focus().wrapSelectionNode(NEW_COMMENT_ID).run();
    }

    return true;
  };
};

export default useCreateSelection;
